import { encode } from "Helpers/enc";
import { toNum } from "Helpers/get-to-num";
import { toaster } from "Helpers/toaster";
import { fetchCustomerList } from "Services/customer.service";
import { fetchCustomerLocationList } from "Services/location.service";
import {
  updateOrder,
  calculateUpdatedOrder,
  fetchDeliveryAndServiceTypeList,
  fetchGoodsCareConditionList,
  fetchOrder,
  createOrder,
  calculateOrder,
} from "Services/order.service";
import { isNil, map } from "lodash";
import useOrderFormStates from "../../../components/order-management/order-create-form/order-form-states";
import { useHistory, useLocation } from "react-router-dom";
import useSession from "Hooks/session.hook";
import { SortingMode } from "Types/common.types";
import { OrderSettingsEnum } from "Data/settings";
import { reverseGeocoding } from "Services/geocoding.services";
import { fetchEstimatedDeliveryDays } from "Services/customer-rate-table.services";
import { routeToOrderView } from "Helpers/route-order-view";
import useQuery from "Hooks/query.hook";

const orderSettingTypesFilter = [
  OrderSettingsEnum.SPECIAL_CARE,
  OrderSettingsEnum.SPECIAL_CONDITION,
  OrderSettingsEnum.TYPE_OF_GOODS,
];

const defaultSorting = { sortBy: "orgName", mode: SortingMode.DESC };

const useOrderFormEdit = (props) => {
  const {
    decodedOrderID,
    sorting = defaultSorting,
    queryParams,
    itemPerPage,
  } = props || {};

  const history = useHistory();
  const location = useLocation();
  const { org } = useSession();

  const {
    setGoodCareConditionList,
    setLocationList,
    setDoesPostcodeExist,
    setDeliveryServiceTypes,
    setCustomerList,
    setEstimatedOrder,
    setOrderEditDetail,
    setShowPrompt,
    setIsLoading,
    setEstimatedDeliveryDays,
    filters,
  } = useOrderFormStates();

  const handleCreateOrder = async (data) => {
    setIsLoading(true);

    const responseData: any = await createOrder(data);

    if (!isNil(responseData) && responseData.success) {
      setShowPrompt && setShowPrompt(true);
    } else {
      toaster("error", "Create Failed");
    }

    setIsLoading(false);
  };

  const handleUpdateOrder = async (data) => {
    if (!decodedOrderID) {
      console.log("Error: order id not found");
      return;
    }

    const responseData: any = await updateOrder(data);

    if (!isNil(responseData) && responseData.success) {
      toaster("success", "Updated Successfully!");
      handleOpenParcelOrderProcessing(decodedOrderID);
    } else {
      toaster("error", "Update Failed");
    }
  };

  const handleCalculateOrder = async (data) => {
    const finalData = {
      ...data,
      orderType: data.parcelType,
      hasInsurance: data.hasInsurance,
      looseOrders: data.looseOrders,
    };

    const responseData: any = await calculateOrder(finalData);

    if (!responseData.error) {
      setEstimatedOrder(responseData);
    } else {
      toaster("error", "Create Failed");
    }
  };

  const handleCalculateUpdatedOrder = async (data) => {
    const finalData = {
      ...data,
      orderType: data.parcelType,
      hasInsurance: data.hasInsurance,
      looseOrders: data.looseOrders,
    };

    const responseData: any = await calculateUpdatedOrder(finalData);

    if (!responseData.error) {
      setEstimatedOrder(responseData);
    } else {
      toaster("error", "Create Failed");
    }
  };

  const handleOpenParcelOrderProcessing = (orderID) => {
    routeToOrderView(orderID, location, history)
  };

  const handleFetchDeliveryServiceList = async (orgID, inputPostCode) => {
    setDoesPostcodeExist(true);
    setIsLoading(true);

    const responseData: any = await fetchDeliveryAndServiceTypeList(
      orgID,
      inputPostCode,
    );

    if (!isNil(responseData)) {
      if (responseData[0].subOptions.length === 0) {
        setDoesPostcodeExist(false);
        setIsLoading(false);
        return false;
      } else {
        setDeliveryServiceTypes(responseData[0].subOptions);
        setIsLoading(false);
        return true;
      }
    } else {
      setIsLoading(false);
      toaster("error", responseData?.error?.message);
    }
  };

  const handleGoodsSpecialCareConditionList = async (customerID) => {
    setIsLoading(true);

    const responseData = await fetchGoodsCareConditionList(
      customerID,
      orderSettingTypesFilter,
    );

    if (responseData && !responseData.error) {
      let orderTypesSettingMap = {};
      map(responseData, (typeObj) => {
        orderTypesSettingMap[typeObj.name] = typeObj.subOptions;
        return typeObj;
      });
      setGoodCareConditionList(orderTypesSettingMap);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading(false);
  };

  const handleFetchCustomerList = async (customerType) => {
    setIsLoading(true);

    let customerFilters = { customerType: customerType?.toUpperCase() };

    const responseData = await fetchCustomerList(customerFilters, org._id);

    if (responseData && !responseData.error) {
      setCustomerList(responseData);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading(false);
  };

  const handleFetchCustomerLocationList = async (customerID) => {
    // const locationFilters = { tempDelete: false };
    setIsLoading(true);

    filters.tempDelete = false;

    const responseData = await fetchCustomerLocationList(
      // locationFilters
      filters,
      customerID,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      sorting,
    );

    if (responseData && !responseData.error) {
      const customerLocations = map(responseData, (resObj) => {
        resObj.locations = resObj;
        return resObj;
      });
      setLocationList(customerLocations);
      handleGoodsSpecialCareConditionList(customerID);
    } else {
      toaster("error", responseData.error.message);
    }
    setIsLoading(false);
  };

  const handleFetchOrderDetails = async (
    orderID: any,
    isEdit: boolean = false,
  ) => {
    setIsLoading(true);

    const responseData = await fetchOrder(orderID, isEdit);

    if (responseData && !responseData.error) {
      setOrderEditDetail(responseData);
    }

    setIsLoading(false);
  };

  const handleFetchEstimatedDeliveryDays = async (
    customerID: string,
    postCode: string,
  ) => {
    setIsLoading(true);

    const responseData = await fetchEstimatedDeliveryDays(customerID, postCode);
    console.log(responseData);
    
    if (responseData && !responseData.error)
      setEstimatedDeliveryDays(responseData);
    else toaster("error", responseData.error.message);

    setIsLoading(false);
  };

  const handleCloseStackScreen = () => {
    if (decodedOrderID) {
      handleOpenParcelOrderProcessing(decodedOrderID);
    } else {
      history.push("/parcels-order-processing/parcels");
    }
  };

  const handleCoordinatesChecking = async (address: any) => {
    setIsLoading(true);

    const responseData = await reverseGeocoding(address);

    if (responseData && !responseData.error) {
      setIsLoading(false);
      return responseData.coordinates;
    }

    setIsLoading(false);
    return "Error: country does not exist";
  };

  return {
    handleCreateOrder,
    handleUpdateOrder,
    handleCalculateOrder,
    handleCalculateUpdatedOrder,
    handleOpenParcelOrderProcessing,
    handleFetchCustomerList,
    handleFetchCustomerLocationList,
    handleGoodsSpecialCareConditionList,
    handleFetchOrderDetails,
    handleFetchDeliveryServiceList,
    handleCloseStackScreen,
    handleCoordinatesChecking,
    handleFetchEstimatedDeliveryDays
  };
};

export default useOrderFormEdit;
